import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from "@/components/ui/command";
import { Loader2 } from "lucide-react";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { useThrottle } from "@/lib/Utils/useThrottle";
import { useParams } from "@tanstack/react-router";
import { fetchQuery, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  GoogleMapsPlaceDetails,
  googleMapsAddressLookupQuery,
  googleMapsPlaceDetailsQuery,
} from "@/app/Address/GraphQL/googleMapsAddressLookupQuery";
import {
  GenericField,
  InputProps,
} from "@/lib/Components/Form/Fields/GenericField";
import { useField } from "formik";
import { UpsertAddressInput, GoogleMapsPlaceQuery } from "@/gql/graphql";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { Input, InputGroup } from "@/components/catalyst/input";
import { keepPreviousData } from "@tanstack/react-query";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";
import { MapContainer, Marker, useMap } from "react-leaflet";
import { BasemapLayer } from "@/components/map/BasemapLayer";

type AddressAutoCompleteProps = InputProps & {
  biasLat?: number | null;
  biasLng?: number | null;
  initialLat?: number | null;
  initialLng?: number | null;
  onCoordsChange?: (coords: { lat: number; lng: number } | null) => void;
  onPlaceSelect?: (
    place: GoogleMapsPlaceDetails,
    signal: AbortSignal,
  ) => Promise<void> | void;
};

function useGeoLocationBias(biasLat?: number | null, biasLng?: number | null) {
  const [geoLocation, setGeoLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  useEffect(() => {
    if (biasLat != null && biasLng != null) return;
    navigator.geolocation?.getCurrentPosition(
      (position) => {
        setGeoLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {},
    );
  }, [biasLat, biasLng]);

  return {
    lat: biasLat ?? geoLocation?.lat,
    lng: biasLng ?? geoLocation?.lng,
  };
}

export function AddressInput({
  name,
  label,
  autoFocus,
  biasLat,
  biasLng,
  initialLat,
  initialLng,
  onCoordsChange,
  onPlaceSelect,
}: AddressAutoCompleteProps) {
  const t = useTranslations("address.address_dialog");
  const [searchInput, setSearchInput] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    initialLat != null && initialLng != null
      ? { lat: initialLat, lng: initialLng }
      : null,
  );
  const geoBias = useGeoLocationBias(biasLat, biasLng);
  const [isLoadingPlace, setIsLoadingPlace] = useState(false);
  const [, , { setValue }] = useField<UpsertAddressInput | null>(name);
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const placeSelection = useRef<AbortController | null>(null);

  return (
    <div className="col-span-full space-y-2">
      {label ? (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      ) : null}
      <div className="relative flex gap-4 rounded-lg border bg-gray-50 p-4">
        {isLoadingPlace ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : null}
        <div className="grid min-w-0 flex-1 grid-cols-12 gap-3">
          <AddressAutoCompleteInput
            autoFocus={autoFocus}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            biasLat={geoBias.lat}
            biasLng={geoBias.lng}
            setPlaceId={async (placeId) => {
              placeSelection.current?.abort();
              const selection = new AbortController();
              placeSelection.current = selection;
              setIsLoadingPlace(true);
              try {
                const placeData = await fetchQuery(
                  googleMapsPlaceDetailsQuery,
                  {
                    placeId,
                  },
                  orgId ?? null,
                );

                if (selection.signal.aborted) {
                  return;
                }

                await onPlaceSelect?.(
                  placeData.googleMapsPlace,
                  selection.signal,
                );

                if (selection.signal.aborted) {
                  return;
                }

                const { coords: placeCoords, ...mapped } =
                  mapAddress(placeData);
                const address = applyVerifiedLocality(
                  mapped,
                  placeData.googleMapsPlace.verifiedLocality,
                );
                setValue(address);
                setCoords(placeCoords);
                onCoordsChange?.(placeCoords);
                setSearchInput(
                  address.address_line_1 ||
                    placeData.googleMapsPlace.formattedAddress,
                );
                focusFirstMissingField(name, address);
              } finally {
                if (!selection.signal.aborted) {
                  setIsLoadingPlace(false);
                }
              }
            }}
            label={t("address_line_1")}
            name={`${name}.address_line_1`}
          />
          <TextInput
            name={`${name}.address_line_2`}
            label={t("address_line_2")}
            className="col-span-full"
            optionalLabel
          />
          <TextInput
            name={`${name}.city`}
            label={t("city")}
            className="col-span-full lg:col-span-6"
          />
          <TextInput
            name={`${name}.state`}
            label={t("state")}
            className="col-span-full lg:col-span-6"
          />
          <TextInput
            name={`${name}.postcode`}
            label={t("postcode")}
            className="col-span-full lg:col-span-6"
          />
          <TextInput
            name={`${name}.country`}
            label={t("country")}
            className="col-span-full lg:col-span-6"
          />
        </div>

        <div className="hidden w-64 shrink-0 lg:block">
          <MapPreview lat={coords?.lat} lng={coords?.lng} />
        </div>
      </div>
    </div>
  );
}

type AddressAutoCompleteInputProps = {
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  setPlaceId: (placeId: string) => Promise<void> | void;
  autoFocus?: boolean;
  biasLat?: number | null;
  biasLng?: number | null;
  label: string;
  name: string;
};

function AddressAutoCompleteInput({
  searchInput,
  setSearchInput,
  setPlaceId,
  autoFocus,
  biasLat,
  biasLng,
  label,
  name,
}: AddressAutoCompleteInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const sessionToken = useMemo(() => crypto.randomUUID(), []);
  const t = useTranslations("address.address_input");
  const [{ value: fieldValue }, {}, { setValue: setFieldValue }] =
    useField<string>(name);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      close();
    }
  };

  const debouncedSearchInput: string = useThrottle(searchInput, 500);

  const shouldEnableQuery = debouncedSearchInput.length > 0 && isOpen;

  const { data, isLoading, isFetching } = useGqlQuery(
    googleMapsAddressLookupQuery,
    {
      input: {
        search: debouncedSearchInput,
        sessiontoken: sessionToken,
        biasLat: biasLat ?? undefined,
        biasLng: biasLng ?? undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
      enabled: shouldEnableQuery,
      staleTime: 30000,
    },
  );

  useEffect(() => {
    if (!searchInput) {
      setIsOpen(false);
    }
  }, [searchInput]);

  const predictions = data?.googleMapsAutocomplete || [];

  const showSpinner = isFetching && debouncedSearchInput.length > 0 && isOpen;

  // Show searchInput when focused (for autocomplete), fieldValue when blurred
  const inputValue = isFocused ? searchInput : (fieldValue ?? "");

  return (
    <GenericField
      name={name}
      label={label}
      viewNode={fieldValue || undefined}
      className="col-span-full"
    >
      <Command
        shouldFilter={false}
        onKeyDown={handleKeyDown}
        className="overflow-visible"
      >
        <InputGroup>
          {showSpinner ? (
            <Spinner data-slot="icon" />
          ) : (
            <MagnifyingGlassIcon data-slot="icon" />
          )}
          <Input
            autoFocus={autoFocus}
            value={inputValue}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setFieldValue(e.target.value);
              if (e.target.value.length > 0) {
                open();
              }
            }}
            onBlur={() => {
              close();
              setIsFocused(false);
            }}
            onFocus={() => {
              setIsFocused(true);
              setSearchInput(fieldValue ?? "");
              if (fieldValue && fieldValue.length > 0) {
                open();
              }
            }}
            placeholder={t("search_placeholder")}
          />
        </InputGroup>

        {isOpen && searchInput.length > 0 && (
          <div className="relative h-auto">
            <CommandList>
              <div className="absolute top-1.5 isolate z-50 w-full">
                <CommandGroup className="bg-background relative h-auto min-w-32 overflow-hidden rounded-md border shadow-md">
                  {isLoading && debouncedSearchInput.length > 0 ? (
                    <div className="flex h-28 items-center justify-center">
                      <Loader2
                        className="size-6 animate-spin"
                        data-slot="icon"
                      />
                    </div>
                  ) : (
                    <>
                      {predictions.map((prediction) => (
                        <CommandPrimitive.Item
                          value={prediction.text.text}
                          onSelect={async () => {
                            close();
                            try {
                              await setPlaceId(prediction.placeId);
                              // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            } catch (e: any) {
                              // noop
                            }
                          }}
                          className="aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-accent hover:text-accent-foreground flex h-max select-text flex-col items-start gap-0.5 rounded-md p-2 px-3 text-xs"
                          key={prediction.placeId}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {prediction.text.text}
                        </CommandPrimitive.Item>
                      ))}
                    </>
                  )}

                  <CommandEmpty>
                    {!showSpinner && predictions.length === 0 && (
                      <div className="flex items-center justify-center py-4">
                        {debouncedSearchInput === ""
                          ? t("search_empty_start")
                          : t("search_no_results")}
                      </div>
                    )}
                  </CommandEmpty>
                </CommandGroup>
              </div>
            </CommandList>
          </div>
        )}
      </Command>
    </GenericField>
  );
}

type MappedAddress = Omit<ReturnType<typeof mapAddress>, "coords">;

type VerifiedLocality = NonNullable<
  GoogleMapsPlaceQuery["googleMapsPlace"]["verifiedLocality"]
>;

/**
 * Google Places returns the suburb as the locality for Australian addresses,
 * so the backend verifies those against G-NAF and maps them onto an Imoova
 * city. An empty city means nothing could be resolved and the user fills it in.
 */
function applyVerifiedLocality(
  mapped: MappedAddress,
  verified: VerifiedLocality | null,
): MappedAddress {
  if (!verified) {
    return mapped;
  }

  return {
    ...mapped,
    city: verified.city,
    state: verified.state ?? mapped.state,
    postcode: verified.postcode ?? mapped.postcode,
  };
}

const REQUIRED_FIELD_ORDER = [
  "address_line_1",
  "city",
  "state",
  "postcode",
  "country",
] as const;

function focusFirstMissingField(
  prefix: string,
  mapped: Pick<
    UpsertAddressInput,
    "address_line_1" | "city" | "state" | "postcode" | "country"
  >,
): void {
  const missing = REQUIRED_FIELD_ORDER.find((key) => !mapped[key]);
  if (!missing) {
    return;
  }
  setTimeout(() => {
    const el = document.querySelector<HTMLInputElement>(
      `[name="${prefix}.${missing}"]`,
    );
    el?.focus();
  }, 0);
}

function MapPreview({
  lat,
  lng,
}: {
  lat?: number | null;
  lng?: number | null;
}) {
  const hasCoords = !!(lat && lng);

  return (
    <div className="overflow-hidden rounded-md">
      <MapContainer
        style={{ height: "100%", width: "100%", minHeight: 200 }}
        zoom={1}
        scrollWheelZoom={false}
        center={[-25.5, 134]}
      >
        <BasemapLayer />
        {hasCoords ? (
          <>
            <Marker position={[lat, lng]} />
            <MapUpdater lat={lat} lng={lng} />
          </>
        ) : null}
      </MapContainer>
    </div>
  );
}

function MapUpdater({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lng], 10);
  }, [map, lat, lng]);

  return null;
}

function mapAddress({ googleMapsPlace }: GoogleMapsPlaceQuery) {
  let address1 = "";
  let postcode = "";
  let city = "";
  let state = "";
  let country = "";
  let addressLine2 = "";
  let neighborhood = "";
  let district = "";

  const processedTypes = new Set<string>();

  for (const component of googleMapsPlace.addressComponents) {
    let componentType = "";

    if (component.types.length === 0) {
      continue;
    }

    if (component.types.includes("premise")) {
      componentType = "premise";
    } else if (component.types.includes("street_number")) {
      componentType = "street_number";
    } else if (component.types.includes("route")) {
      componentType = "route";
    } else if (component.types.includes("subpremise")) {
      componentType = "subpremise";
    } else {
      componentType = component.types[0];
    }

    if (processedTypes.has(componentType)) {
      continue;
    }

    processedTypes.add(componentType);

    switch (componentType) {
      case "premise":
        address1 = component.longText;
        break;

      case "street_number": {
        address1 = address1
          ? `${component.longText} ${address1}`
          : component.longText;
        break;
      }

      case "route": {
        address1 = address1
          ? `${address1} ${component.longText}`
          : component.longText;
        break;
      }

      case "postal_code": {
        postcode = component.longText;
        break;
      }

      case "postal_code_suffix": {
        postcode = postcode
          ? `${postcode}-${component.longText}`
          : component.longText;
        break;
      }

      case "locality":
        city = component.longText;
        break;

      case "colloquial_area":
        if (!city) {
          city = component.longText;
        }
        break;

      case "sublocality":
      case "sublocality_level_1":
        if (!city) {
          city = component.longText;
        }
        break;

      case "neighborhood":
      case "sublocality_level_2":
      case "sublocality_level_3":
        neighborhood = component.longText;
        break;

      case "administrative_area_level_1": {
        state = component.shortText ?? "";
        break;
      }

      case "administrative_area_level_2": {
        district = component.longText;
        break;
      }

      case "administrative_area_level_3":
      case "administrative_area_level_4":
      case "administrative_area_level_5":
        if (!city && !district) {
          district = component.longText;
        }
        break;

      case "country":
        country = component.longText;
        break;

      case "subpremise":
        addressLine2 = component.longText;
        break;

      case "floor":
        addressLine2 = addressLine2
          ? `${addressLine2}, Floor: ${component.longText}`
          : `Floor: ${component.longText}`;
        break;

      case "room":
        addressLine2 = addressLine2
          ? `${addressLine2}, Room: ${component.longText}`
          : `Room: ${component.longText}`;
        break;

      case "post_box":
        addressLine2 = addressLine2
          ? `${addressLine2}, PO Box: ${component.longText}`
          : `PO Box: ${component.longText}`;
        break;
    }
  }

  if (!address1 && neighborhood) {
    address1 = neighborhood;
  }

  if (!city && district) {
    city = district;
  }

  // For places without street-level detail (e.g. small towns),
  // fall back to the locality name as address_line_1.
  if (!address1 && city) {
    address1 = city;
  }

  return {
    address_line_1: address1,
    address_line_2: addressLine2,
    city,
    state,
    postcode,
    country,
    coords: {
      lat: googleMapsPlace.location.latitude,
      lng: googleMapsPlace.location.longitude,
    },
  };
}
